// The words a reviewer reads on a review item (D86/D93): a plain title per
// reason code, the kind badge, the record and field in doubt, and the rules
// for when Accept is offered. Written for a low-attention reader: what the
// problem is, then what to do.
import { ReviewCandidate, ReviewItemKind, ReviewLineageKind, ReviewReasonCode, ReviewSource } from '@mono/proto-clients';
import { isMoneyField } from '../../lib/correctedValue';
import { formatMoney, optionalTaskLabel, periodLabel } from '../../lib/recordLabels';
import { reviewSourceProps } from '../../lib/reviewSourceProps';
import { parseSpan } from '../../lib/sourceSpan';
// Every code has a title; the Record type makes a missing one a type error and
// the copy test makes it a failing test.
export const REASON_TITLES = {
    [ReviewReasonCode.UNSPECIFIED]: 'This needs a look',
    [ReviewReasonCode.CONTRACT_TYPE_CONFLICT]: 'The documents disagree on the contract type',
    [ReviewReasonCode.POP_NOT_READ]: "We couldn't read this line's period of performance",
    [ReviewReasonCode.MOD_EFFECTIVE_DATE_NOT_STATED]: "This modification doesn't state an effective date",
    [ReviewReasonCode.EXERCISE_DATE_NOT_STATED]: "This option exercise doesn't say when it was exercised",
    [ReviewReasonCode.POP_CHANGE_UNTARGETED]: "A period change doesn't say which period it applies to",
    [ReviewReasonCode.SCOPE_NAME_CLASH]: 'Two labor-category names clash, so the scope name is open',
    [ReviewReasonCode.SPLIT_NOT_SUMMING]: "The parts of a split don't add up to the stated total",
    [ReviewReasonCode.MOD_TYPE_UNDER_GATE]: "We're not sure what type of modification this is",
    [ReviewReasonCode.MOD_PURPOSE_UNDER_GATE]: "We're not sure what this modification is for",
    [ReviewReasonCode.MOD_APPLIED_WITHOUT_COMPARISON]: 'This modification was recorded without a line-by-line check',
    [ReviewReasonCode.ADD_EXISTING_LINE_DIFFERS]: 'A modification adds a line that already exists, with different values',
    [ReviewReasonCode.LINE_IDENTITY_UNRESOLVED]: "We can't tell which line this belongs to",
    [ReviewReasonCode.FIELD_SHAPE_INVALID]: "A line or section number doesn't look right",
    [ReviewReasonCode.CLIN_SEQUENCE_GAP]: 'A line number is missing from the sequence',
    [ReviewReasonCode.DANGLING_PWS_REFERENCE]: "A requirement cites a PWS section that isn't in the document",
    [ReviewReasonCode.CITED_ATTACHMENT_ABSENT]: "A requirement cites an attachment we don't have",
    [ReviewReasonCode.LATER_DOCUMENT_CONTRADICTS]: 'A later document disagrees with a value that was already settled',
    [ReviewReasonCode.COMPLETION_DATE_CLAIM_DIFFERS]: "The modification's completion date disagrees with the lines",
    [ReviewReasonCode.PERIOD_FUNDING_UNRESOLVED]: "We can't tell which period this funding belongs to",
    [ReviewReasonCode.OPTION_PERIOD_FUNDED_NOT_EXERCISED]: 'This is funded, but no document says it was exercised',
    [ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES]: 'The document gives two different values for this',
    [ReviewReasonCode.MOD_LINE_SCHEDULE_UNREAD]: "We couldn't read this modification's line schedule",
    [ReviewReasonCode.MOD_OBLIGATION_TOTAL_DIFFERS]: "This modification's funding doesn't add up to the total it states",
    [ReviewReasonCode.MOD_LINE_DELIVERABLES_UNHANDLED]: "This modification's new lines list deliverables we don't schedule from it",
    [ReviewReasonCode.EXERCISE_WINDOW_NOT_STATED]: "This exercise doesn't say which period the task is exercised for",
    [ReviewReasonCode.OCCURRENCE_OWNER_UNRESOLVED]: 'Which team filed this?',
    [ReviewReasonCode.DUE_RULE_UNDER_GATE]: "We're not sure when this deliverable is due",
    [ReviewReasonCode.PROSE_VERB_LINK_UNDECIDED]: "We're not sure this sentence asks for a deliverable",
    [ReviewReasonCode.CROSS_REFERENCE_SUBJECT_DIFFERS]: "A line cites a section that's about something else",
    [ReviewReasonCode.CONDITION_BRANCH_UNRESOLVED]: "The award doesn't say which of its conditions applies",
    [ReviewReasonCode.DUTY_PERIOD_NOT_STATED]: "Deliverables are scheduled for a period the contract doesn't state",
    [ReviewReasonCode.LINE_SNAPSHOT_FROZEN]: 'Lines kept as they were when the award was re-read',
    [ReviewReasonCode.PERIOD_NO_LONGER_IN_DOCUMENT]: 'A period is no longer in the document. Remove it?',
    [ReviewReasonCode.KEEP_DECISION]: 'The document changed after you decided. Keep your decision?',
    [ReviewReasonCode.REVIEWER_ADDED_LINE]: 'A reviewer added a missing line',
    [ReviewReasonCode.REVIEWER_CORRECTED_LINE]: 'A reviewer corrected this line'
};
export function picksTeam(review) {
    if (review.reasonCode === ReviewReasonCode.SCOPE_NAME_CLASH) return false;
    const field = review.targetFieldPath.trim().toLowerCase();
    const type = review.targetObjectType.trim().toLowerCase();
    return field === 'workstream_id' || field === 'team_id' || type.includes('workstream') || type.includes('team');
}
export function isOccurrenceOwner(review) {
    return review.reasonCode === ReviewReasonCode.OCCURRENCE_OWNER_UNRESOLVED;
}
// Code 26's sources carry each filed due date (YYYY-MM-DD); the deliverable
// name is not on the wire, so the record reads "Filed deliverable" rather than
// the occurrence row id.
export function occurrenceOwnerSummary(review) {
    const dates = review.sources.map((source)=>source.quote.trim()).filter(Boolean).map((quote)=>formatReviewDay(quote) ?? quote);
    const listed = dates.length > 1 ? `${dates.slice(0, -1).join(', ')} and ${dates[dates.length - 1]}` : dates[0];
    return {
        record: 'Filed deliverable',
        dates: listed ?? 'Not stated'
    };
}
// A code whose plain title is written for one kind of record reads wrong on
// another: POP_NOT_READ was a line's, and the period locator (SAD-778) raises
// it on a period too. Those titles name the record instead. LINE_SNAPSHOT_FROZEN
// on period_snapshot is the anti-gutting freeze for vanished periods (SAD-791).
function targetTitle(review) {
    // A finding with no field of its own (a document-level note) or a missing
    // document is only as specific as its reason: the reason is the title
    // ("Optional Task One: printed unfunded $… ≠ total − funded $…").
    if ((review.targetFieldPath === FINDING_FIELD || review.targetObjectType === 'Document') && review.reason.trim()) {
        return reasonHeadline(review.reason);
    }
    // A date printed two ways: the reason names both printings ("Start printed
    // as 9 Mar 2025, …; page 2 prints 9 Mar 2026"), which is the problem.
    if (review.reasonCode === ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES && review.targetFieldPath.endsWith('_date') && review.reason.trim()) {
        return reasonHeadline(review.reason);
    }
    if (review.reasonCode === ReviewReasonCode.POP_NOT_READ && review.targetObjectType === 'PeriodOfPerformance') {
        return `Period of performance not read for ${periodLabel(review.targetLocator)}`;
    }
    if (review.reasonCode === ReviewReasonCode.LINE_SNAPSHOT_FROZEN && review.targetFieldPath === 'period_snapshot') {
        return "The new import didn't propose removing periods";
    }
    if (review.reasonCode === ReviewReasonCode.CONTRACT_TYPE_CONFLICT && !disagrees(review)) {
        return 'No contract type is stated for this line';
    }
    return undefined;
}
const FINDING_FIELD = 'finding';
// The reason's first sentence, as a row title.
function reasonHeadline(reason, max = 140) {
    const t = reason.trim();
    const end = t.search(/\.(\s|$)/);
    const first = end > 0 ? t.slice(0, end) : t;
    return first.length > max ? `${first.slice(0, max - 1)}…` : first;
}
// A review zou could not decode arrives with no kind or code and a reason
// saying so; it reads as "This needs a look" with that reason as the body.
export function reviewTitle(review) {
    return targetTitle(review) ?? REASON_TITLES[review.reasonCode] ?? REASON_TITLES[ReviewReasonCode.UNSPECIFIED];
}
export function kindLabel(kind, targetObjectType = '') {
    if (kind === ReviewItemKind.CONFLICT) return 'Documents disagree';
    if (kind === ReviewItemKind.IDENTITY) {
        if (targetObjectType === 'PeriodOfPerformance') return 'Which period?';
        if (targetObjectType === 'OptionalTask') return 'Which task?';
        // Code 26 targets the filed Occurrence; its question is the team.
        if (targetObjectType === 'Workstream' || targetObjectType === 'Team' || targetObjectType === 'Occurrence') {
            return 'Which team?';
        }
        return 'Which line?';
    }
    return 'Needs a look';
}
// A conflict is a disagreement only when the documents give two values; one
// with fewer (no type stated, a date not read) is a value nobody stated.
export function disagrees(review) {
    return review.candidates.length >= 2;
}
const NOT_STATED_CODES = new Set([
    ReviewReasonCode.MOD_EFFECTIVE_DATE_NOT_STATED,
    ReviewReasonCode.EXERCISE_DATE_NOT_STATED,
    ReviewReasonCode.EXERCISE_WINDOW_NOT_STATED,
    ReviewReasonCode.DUTY_PERIOD_NOT_STATED
]);
const NOT_SURE_CODES = new Set([
    ReviewReasonCode.MOD_TYPE_UNDER_GATE,
    ReviewReasonCode.MOD_PURPOSE_UNDER_GATE,
    ReviewReasonCode.DUE_RULE_UNDER_GATE,
    ReviewReasonCode.PROSE_VERB_LINK_UNDECIDED
]);
const LOOKS_WRONG_CODES = new Set([
    ReviewReasonCode.FIELD_SHAPE_INVALID,
    ReviewReasonCode.CLIN_SEQUENCE_GAP,
    ReviewReasonCode.DANGLING_PWS_REFERENCE,
    ReviewReasonCode.CROSS_REFERENCE_SUBJECT_DIFFERS
]);
// The badge a reviewer reads first: what kind of problem this is, in the
// reason code's own words. "Documents disagree" only where two values are on
// the table.
export function badgeLabel(review) {
    if (review.kind === ReviewItemKind.IDENTITY) {
        return kindLabel(review.kind, review.targetObjectType);
    }
    const code = review.reasonCode;
    if (NOT_STATED_CODES.has(code)) return 'Not stated';
    if (NOT_SURE_CODES.has(code)) return 'Not sure';
    if (LOOKS_WRONG_CODES.has(code)) return 'Looks wrong';
    if (CHECK_ONLY_CODES.has(code)) return 'Check it';
    switch(code){
        case ReviewReasonCode.POP_NOT_READ:
            return 'Not confirmed';
        case ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES:
            return 'Two values printed';
        case ReviewReasonCode.SPLIT_NOT_SUMMING:
            return "Doesn't add up";
        case ReviewReasonCode.CONDITION_BRANCH_UNRESOLVED:
            return 'Which applies?';
        case ReviewReasonCode.LINE_SNAPSHOT_FROZEN:
            return 'For your information';
        case ReviewReasonCode.REVIEWER_ADDED_LINE:
        case ReviewReasonCode.REVIEWER_CORRECTED_LINE:
            return 'Reviewer change';
        case ReviewReasonCode.UNSPECIFIED:
            return kindLabel(review.kind, review.targetObjectType);
        default:
            return disagrees(review) ? 'Documents disagree' : 'Not stated';
    }
}
export function kindKey(kind) {
    if (kind === ReviewItemKind.CONFLICT) return 'conflict';
    if (kind === ReviewItemKind.IDENTITY) return 'identity';
    return 'unknown';
}
// "CLIN 0001" / "Modification P00003" / "Contract" / "Option Period 2".
export function recordLabel(type, locator) {
    const l = locator.trim();
    switch(type){
        case 'ContractCLIN':
        case 'CLIN':
            return l ? `CLIN ${l}` : 'A contract line (not yet known)';
        case 'ProgramModification':
            return l ? `Modification ${l}` : 'A modification with no number';
        case 'Program':
            return 'Contract';
        case 'PeriodOfPerformance':
            return l ? periodLabel(l) : 'A period (not yet known)';
        case 'OptionalTask':
            return l ? optionalTaskLabel(l) : 'An optional task (not yet known)';
        case 'DutyCondition':
            return l ? `PWS ${l} conditions` : 'A PWS condition';
        default:
            return l ? `${type} ${l}` : type || 'Contract';
    }
}
const FIELD_LABELS = {
    contract_type: 'Contract type',
    funded_amount: 'Funded amount',
    total_amount: 'Total amount',
    pop_start_date: 'Period of performance start',
    pop_end_date: 'Period of performance end',
    start_date: 'Start date',
    end_date: 'End date',
    effective_date: 'Effective date',
    exercised: 'Exercised',
    modification_type: 'Modification type',
    purpose: 'Purpose',
    optional_task_number: 'Optional task',
    clin: 'CLIN family',
    funded_amount_contribution: 'Funding'
};
// "funded_amount" → "Funded amount"; an empty field is the whole record.
export function fieldLabel(field) {
    const f = field.trim();
    if (!f) return 'The whole record';
    return FIELD_LABELS[f] ?? f.replace(/_/g, ' ').replace(/^\w/, (c)=>c.toUpperCase());
}
// The row id a reviewer's target was stamped with. zou writes the locator as
// {fieldPath: objectId} (targetToLocatorJSON), so that named key is the row.
export function targetRowId(d) {
    try {
        const loc = JSON.parse(d.targetRecordLocatorJson || '{}');
        const v = loc[d.review?.targetFieldPath ?? ''];
        return typeof v === 'string' ? v : '';
    } catch  {
        return '';
    }
}
// The record a review item is on, named from the program's own rows where
// they are loaded. A period's locator is the natural key pangaea derived from
// the row, and a period added by hand with no option number derives as
// "base" whatever its type and name say — so a period is named by its row,
// the way the Periods table names it, and the key is only the fallback.
export function recordLabelFor(d, periods = []) {
    const r = d.review;
    if (isOccurrenceOwner(r)) return occurrenceOwnerSummary(r).record;
    if (targetKind(r.targetObjectType) === 'period' && periods.length > 0) {
        // The stamped target is a row id once a reviewer picked it, but an
        // extraction may have left a natural key ("base", "option:2") there —
        // pangaea's resolvePoPTarget reads both — so both resolve as keys.
        const rowId = periodIdForKey(targetRowId(d), periods) || periodIdForKey(r.targetLocator, periods);
        const row = rowId ? periods.find((p)=>p.id === rowId) : undefined;
        if (row?.periodName.trim()) return row.periodName.trim();
    }
    return recordLabel(r.targetObjectType, r.targetLocator);
}
// The row subtitle: "CLIN 0001 · Contract type".
export function targetSummary(d, periods = []) {
    const record = recordLabelFor(d, periods);
    if (isOccurrenceOwner(d.review)) return `${record} · Due ${occurrenceOwnerSummary(d.review).dates}`;
    const field = d.review.targetFieldPath;
    if (field === FINDING_FIELD) {
        // "Contract · Finding" says nothing; where it was read does.
        const page = firstPage(d.review.sources);
        return page > 0 ? `${record} · page ${page}` : record;
    }
    return field ? `${record} · ${fieldLabel(field)}` : record;
}
export function targetKind(type) {
    switch(type){
        case 'PeriodOfPerformance':
            return 'period';
        case 'ContractCLIN':
        case 'CLIN':
            return 'line';
        case 'OptionalTask':
            return 'task';
        default:
            return null;
    }
}
export function canAssignTarget(type) {
    return targetKind(type) !== null;
}
// Whether settling an identity item means picking a row. A line's optional
// task (LINE_IDENTITY_UNRESOLVED on optional_task_number, K9b-2) is answered
// with a value on that line instead: one of its family's tasks, or "not a
// task", picked like any other candidate.
export function picksTarget(review) {
    if (review.kind !== ReviewItemKind.IDENTITY) return false;
    if (picksTeam(review)) return false;
    return !(targetKind(review.targetObjectType) === 'line' && review.targetFieldPath.trim() === 'optional_task_number');
}
// Settled for the commit gate — the same list as zou's validateCommitInvariants
// and pangaea's refuseOpenReviewItems.
export const SETTLED_STATES = new Set([
    'Accepted',
    'Corrected',
    'Waived',
    'Closed',
    'Applied',
    'Acknowledged'
]);
export function isReviewItem(d) {
    return !!d.review;
}
export function isOpenReviewItem(d) {
    return isReviewItem(d) && !SETTLED_STATES.has(d.resolutionState);
}
export function acceptBlocker(d) {
    const r = d.review;
    const field = fieldLabel(r.targetFieldPath).toLowerCase();
    // zou projects pangaea's settle-write table (reviewitem.SettleWritable) on
    // each item: an accepted item that writes nothing refuses the apply (D87),
    // so Accept is offered only where the wire says the value is written.
    if (!d.reviewSettleWrite) {
        if (isCheckOnly(r)) {
            return {
                kind: 'noSettleWrite',
                message: 'Nothing is written for this. Check it against the document, then close it with a ' + 'reason. Commit stays off until you do.'
            };
        }
        const what = r.targetFieldPath.trim() ? field : 'an answer to this';
        return {
            kind: 'noSettleWrite',
            message: `This can't be applied yet: Specra can't write ${what} back. ` + 'Leave it open (commit stays off) or leave it blank with a reason.'
        };
    }
    if (picksTarget(r)) {
        if (!canAssignTarget(r.targetObjectType)) {
            return {
                kind: 'targetUnavailable',
                message: "Specra can't assign this to a line or task yet. Leave it open (commit stays off) " + 'or leave it blank with a reason.'
            };
        }
        // An identity item's value rides the change item's proposed_value_json;
        // with none there is nothing to write once the target is picked.
        if (!hasJsonValue(d.proposedValueJson)) {
            return {
                kind: 'noValue',
                message: `This can't be applied yet: the documents give Specra no ${field} to write. ` + 'Leave it open (commit stays off) or leave it blank with a reason.'
            };
        }
    }
    return null;
}
// An ordinary change item no writer lands (no_writer on the wire, the
// writable-shape table pangaea's strict apply refuses by — SAD-700): Accept
// and Corrected are not offered; the non-writing decisions remain available.
export const NO_WRITER_NOTE = "Specra can't write this change to the contract; leave it blank or record it by hand.";
export function hasNoWriter(d) {
    return !!d.noWriter && !d.review;
}
// The decisions a change item offers: every one for a writable item; only the
// non-writing decisions for one no writer lands.
export function offeredDecisions(d, all) {
    return hasNoWriter(d) ? all.filter((s)=>s === 'Waived' || s === 'Rejected' || s === 'Escalated') : all;
}
// A no-writer item is settled only by a decision that deliberately writes
// nothing. Accepted/Corrected may predate no_writer projection and must be
// revisited rather than allowing commit to reach strict apply.
export function noWriterNeedsDecision(d) {
    return hasNoWriter(d) && ![
        'Waived',
        'Rejected',
        'Escalated'
    ].includes(d.resolutionState);
}
// What Approve all leaves for the reviewer: the no-writer items it skipped.
export function approveAllSkippedNote(skipped) {
    return `${skipped} item${skipped === 1 ? '' : 's'} skipped: Specra can't write ${skipped === 1 ? 'it' : 'them'} to the contract; leave ${skipped === 1 ? 'it' : 'each'} blank or record ${skipped === 1 ? 'it' : 'them'} by hand.`;
}
export function hasJsonValue(s) {
    if (!s) return false;
    try {
        const v = JSON.parse(s);
        return v !== null && v !== '';
    } catch  {
        return s.trim() !== '';
    }
}
function parseObject(json) {
    try {
        const v = JSON.parse(json);
        return typeof v === 'object' && v !== null && !Array.isArray(v) ? v : null;
    } catch  {
        return null;
    }
}
function hasExactlyKeys(o, keys) {
    const have = Object.keys(o);
    return have.length === keys.length && keys.every((k)=>k in o);
}
// The object shapes a review value is known to take, as the rows a reviewer
// reads. Null for anything else: an unknown shape is shown as JSON, behind a
// Details toggle, never as a labelled guess. A shape matches only when it has
// exactly its known fields, so an extra field is never hidden from the reviewer.
export function valueRows(json) {
    const o = parseObject(json);
    if (!o) return null;
    // PERIOD_FUNDING_UNRESOLVED: the line and the funding change it would place
    // (pangaea's periodFundingValue).
    if (hasExactlyKeys(o, [
        'line',
        'funded_delta'
    ]) && typeof o.line === 'string' && typeof o.funded_delta === 'number') {
        return [
            {
                label: 'Line',
                value: o.line
            },
            {
                label: 'Funding change',
                value: formatMoney(o.funded_delta)
            }
        ];
    }
    return null;
}
// Whether a value is an object or array with no known shape: the raw-JSON case.
// field lets known shapes that need their path (exercise_window) count as known.
export function isUnknownShape(json, field = '') {
    if (isExerciseWindowField(field) && formatExerciseWindow(json)) return false;
    try {
        const v = JSON.parse(json);
        return typeof v === 'object' && v !== null && valueRows(json) === null;
    } catch  {
        return false;
    }
}
const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
// "6 Jul 2026" from a YYYY-MM-DD day (UTC calendar day, never local).
function formatReviewDay(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const written = new Date(Date.UTC(year, month - 1, day));
    if (written.getUTCFullYear() !== year || written.getUTCMonth() !== month - 1 || written.getUTCDate() !== day) {
        return null;
    }
    return `${day} ${MONTHS_SHORT[month - 1]} ${year}`;
}
// SAD-788: OptionalTask/<n>/exercise_window arrives as
// {"start":"YYYY-MM-DD","end":"YYYY-MM-DD"} (end may be absent). Render a
// range a reviewer can read, not raw JSON.
function formatExerciseWindow(json) {
    const o = parseObject(json);
    if (!o) return null;
    const keys = Object.keys(o);
    if (!keys.every((k)=>k === 'start' || k === 'end')) return null;
    // A boundary that is present but not a string is not a window we can read:
    // leave it to the raw-JSON details view.
    if (keys.some((k)=>typeof o[k] !== 'string')) return null;
    const start = typeof o.start === 'string' ? o.start : '';
    const end = typeof o.end === 'string' ? o.end : '';
    if (!start && !end) return null;
    const startLabel = start ? formatReviewDay(start) : null;
    const endLabel = end ? formatReviewDay(end) : null;
    if (start && !startLabel) return null;
    if (end && !endLabel) return null;
    if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
    if (startLabel) return `from ${startLabel}`;
    if (endLabel) return `until ${endLabel}`;
    return null;
}
function isExerciseWindowField(field) {
    return /(^|\/)exercise_window$/i.test(field.trim());
}
// A JSON value as the reviewer reads it, on one line. Money on a money field
// is always cents ("$4,000,000.40", never "4,000,000.4").
export function displayValue(json, field = '') {
    if (!json) return '—';
    if (isExerciseWindowField(field)) {
        const window = formatExerciseWindow(json);
        if (window) return window;
    }
    const rows = valueRows(json);
    if (rows) return rows.map((r)=>`${r.label} ${r.value}`).join(' · ');
    try {
        const v = JSON.parse(json);
        if (typeof v === 'number') return isMoneyField(field) ? formatMoney(v) : v.toLocaleString();
        if (typeof v === 'string' && formatReviewDay(v)) return formatReviewDay(v);
        // A line's window ("2026-03-09/2026-10-31"): both ends as days.
        if (typeof v === 'string') {
            const [start, end] = v.split('/');
            const s = start && formatReviewDay(start);
            const e = end && formatReviewDay(end);
            if (s && e && v.split('/').length === 2) return `${s} – ${e}`;
        }
        return typeof v === 'object' && v !== null ? 'Structured value' : String(v);
    } catch  {
        return json;
    }
}
export function firstPage(sources) {
    return sources.find((s)=>s.page > 0)?.page ?? 0;
}
// The audit rationale written for the reviewer, with their optional note.
export function pickRationale(c, note, field = '') {
    const page = firstPage(c.sources);
    return withNote(`Picked "${c.label || displayValue(c.valueJson, field)}"${page ? ` (p.${page})` : ''}`, note);
}
export function enteredRationale(raw, note) {
    return withNote(`Entered ${raw}`, note);
}
export function assignedRationale(targetLabel, note) {
    return withNote(`Assigned to ${targetLabel}`, note);
}
// A date the reviewer filled with the "Use today's date" suggestion.
export function todayRationale(isoDate, note) {
    return withNote(`Reviewer set today's date (${isoDate})`, note);
}
// "Leave blank" is recorded as the Waived resolution, which users never see:
// waiver has its own meaning in contracting. The reason is required.
export const LEFT_BLANK = 'Left blank: not stated';
// An option funded with no exercise on file is answered "Funded, not
// exercised" (D116), stored the same way: nothing is written, the money
// still counts.
export const FUNDED_NOT_EXERCISED = 'Funded, not exercised';
const LEAVE_BLANK_COPY = {
    label: LEFT_BLANK,
    toggle: 'Leave it blank…',
    action: 'Leave blank',
    hint: 'Nothing is written for this field; the item stops blocking commit.',
    prompt: 'Why leave it blank?',
    placeholder: "For example, the document doesn't say.",
    settled: `${LEFT_BLANK}. Nothing is written for this field.`
};
const ONE_FILING_FOR_EVERY_TEAM_COPY = {
    label: 'One filing stands for every team',
    toggle: 'Leave it as one filing for every team…',
    action: 'Leave blank',
    hint: 'The existing filing stands for every team; the item stops blocking commit.',
    prompt: 'Why should one filing stand for every team?',
    placeholder: 'For example, the same filing covers every team.',
    settled: 'One filing stands for every team.'
};
const FUNDED_NOT_EXERCISED_COPY = {
    label: FUNDED_NOT_EXERCISED,
    toggle: `${FUNDED_NOT_EXERCISED}…`,
    action: FUNDED_NOT_EXERCISED,
    hint: 'Nothing is marked exercised. The money still counts, and the item stops blocking commit.',
    prompt: 'Why is it not exercised?',
    placeholder: 'For example, this is incremental funding; no exercise is on file.',
    settled: `${FUNDED_NOT_EXERCISED}. The money counts; nothing is marked exercised.`
};
// A check on the whole modification (its line schedule went unread, or its
// funding doesn't add up to its stated total) has no value to write: the
// reviewer checks it and closes it, stored the same way as a blank.
export const CHECKED = 'Checked';
const CHECKED_COPY = {
    label: CHECKED,
    toggle: 'Close it…',
    action: 'Close',
    hint: 'Nothing is written; the item stops blocking commit.',
    prompt: 'What did you check?',
    placeholder: 'For example, checked the funding lines against the PDF.',
    settled: `${CHECKED}. Nothing is written.`,
    toast: 'Closed'
};
// Every code whose finding has no value to write (its field has no
// settle-write): the reviewer checks it against the document and closes it.
const CHECK_ONLY_CODES = new Set([
    ReviewReasonCode.MOD_LINE_SCHEDULE_UNREAD,
    ReviewReasonCode.MOD_OBLIGATION_TOTAL_DIFFERS,
    ReviewReasonCode.MOD_APPLIED_WITHOUT_COMPARISON,
    ReviewReasonCode.CITED_ATTACHMENT_ABSENT
]);
// What the reviewer does about a finding that has no value to pick: a check
// to make, or a notice to acknowledge.
const NEXT_STEP = {
    [ReviewReasonCode.MOD_LINE_SCHEDULE_UNREAD]: "Open the modification and check its line schedule against this contract's lines. Correct " + 'any line that is off, then close this with what you checked.',
    [ReviewReasonCode.MOD_OBLIGATION_TOTAL_DIFFERS]: 'Open the modification and add up its funding lines against the total it states. Correct any ' + 'line that is off, then close this with what you checked.',
    [ReviewReasonCode.MOD_APPLIED_WITHOUT_COMPARISON]: "Open the modification and check what it changed against this contract's lines and periods. " + 'Correct anything that is off by hand, then close this with what you checked.',
    [ReviewReasonCode.CITED_ATTACHMENT_ABSENT]: "Upload the missing attachment to this contract's documents, or add the dates by hand. Then " + 'close this with what you did.',
    [ReviewReasonCode.LINE_SNAPSHOT_FROZEN]: 'Nothing was lost or changed: the lines on file stay as they are. Acknowledge this to ' + 'continue. If a line is wrong, use "Correct this line" in this review.'
};
export function nextStep(review) {
    return NEXT_STEP[review.reasonCode] ?? '';
}
export function isCheckOnly(review) {
    return !!review && CHECK_ONLY_CODES.has(review.reasonCode);
}
// An untargeted period date whose every period would be wrong (a completion
// date read as a period end): the honest answer is "none of these".
const NO_PERIOD_COPY = {
    label: 'Not a period date',
    toggle: 'None of these periods…',
    action: 'None of these',
    hint: 'Nothing is written to any period; the item stops blocking commit.',
    prompt: "Why doesn't this date belong to a period?",
    placeholder: 'For example, it is the ultimate completion date, not a period end.',
    settled: 'Not a period date. Nothing is written to any period.'
};
export function blankCopy(review) {
    if (isCheckOnly(review)) return CHECKED_COPY;
    if (review?.reasonCode === ReviewReasonCode.POP_CHANGE_UNTARGETED) return NO_PERIOD_COPY;
    if (review && isOccurrenceOwner(review)) return ONE_FILING_FOR_EVERY_TEAM_COPY;
    return review?.reasonCode === ReviewReasonCode.OPTION_PERIOD_FUNDED_NOT_EXERCISED ? FUNDED_NOT_EXERCISED_COPY : LEAVE_BLANK_COPY;
}
export function leftBlankRationale(reason, review) {
    return `${blankCopy(review).label}. ${reason.trim()}`;
}
// A resolution state as a reviewer reads it.
export function resolutionLabel(state, review) {
    return state === 'Waived' ? blankCopy(review).label : state;
}
// The toast after a decision is saved.
export function resolvedToast(state) {
    if (state === 'Waived') return 'Left blank';
    if (state === 'Rejected' || state === 'Escalated') return `Marked ${state.toLowerCase()}`;
    return 'Decision saved';
}
// Today in the reviewer's own time zone, as the YYYY-MM-DD a date input takes.
export function todayISO(now = new Date()) {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
function withNote(auto, note) {
    const n = note.trim();
    return n ? `${auto}. ${n}` : auto;
}
// The conflict candidate the document's own evidence recommends: the one
// candidate scored as recommended (0.9 and over) above every other. It is
// pre-selected, so the reviewer confirms it with one click; none otherwise.
export function recommendedCandidateKey(review) {
    if (picksTarget(review) || picksTeam(review)) return '';
    const top = review.candidates.filter((c)=>c.score >= 0.9);
    return top.length === 1 ? top[0].key : '';
}
// Identity candidates carry a score; the reader sees words, never a number.
export function likelihood(score) {
    if (score >= 0.5) return 'likely';
    if (score > 0) return 'possible';
    return '';
}
// The lines a later document raised the same finding on, recorded against
// this item instead (D96): settling this item settles them.
export function affectedLines(review) {
    const out = [];
    for (const l of review.lineage){
        if (l.kind === ReviewLineageKind.AFFECTED && l.locator && !out.includes(l.locator)) {
            out.push(l.locator);
        }
    }
    return out;
}
export function gateNote(open) {
    return `Commit is off: ${open} fact${open === 1 ? '' : 's'} need${open === 1 ? 's' : ''} your call. ` + "The documents disagree or don't say which line a value belongs to, and nothing is " + 'applied until each is settled.';
}
// A period natural key ("base", "option:2") → that period's row id, which is
// what SetDiscrepancyTargetPeriod takes. Mirrors pangaea's parsePeriodNaturalKey.
export function periodIdForKey(key, periods) {
    const k = key.trim().toLowerCase();
    if (k === 'base') return periods.find((p)=>p.periodType === 'base')?.id ?? '';
    if (k.startsWith('option:')) {
        const n = Number(k.slice('option:'.length));
        return periods.find((p)=>p.periodType !== 'base' && p.optionNumber === n && Number.isInteger(n))?.id ?? '';
    }
    return periods.find((p)=>p.id === key)?.id ?? '';
}
// A line's CLIN number (or row id) → that current line's row id.
export function lineIdForKey(key, lines) {
    const k = key.trim().toUpperCase();
    return lines.find((l)=>l.clinNumber.trim().toUpperCase() === k || l.id === key)?.id ?? '';
}
// A task number (or row id) → that optional task's row id.
export function taskIdForKey(key, tasks) {
    const n = Number(key.trim());
    return tasks.find((t)=>Number.isInteger(n) && n > 0 && t.taskNumber === n || t.id === key)?.id ?? '';
}
// An identity candidate's value_json is the target's natural key as JSON.
export function candidateKey(c) {
    try {
        const v = JSON.parse(c.valueJson);
        if (typeof v === 'string') return v;
    } catch  {
    /* fall through */ }
    return c.valueJson || c.key;
}
// ---------------------------------------------------------------------------
// Decidable items: the question, its real choices, the evidence and what the
// decision writes.
// ---------------------------------------------------------------------------
// The contract types pangaea writes on a line (clinContractTypes in
// delivery_contract_clin_contract_type.go). Labor-hour is written as T&M
// there, so it is one choice. A value outside this list refuses at apply, so
// a line's type is picked, never typed.
export const CONTRACT_TYPE_CHOICES = [
    {
        value: 'FFP',
        label: 'Firm-fixed price (FFP)'
    },
    {
        value: 'T&M',
        label: 'Time and materials or labor-hour (T&M)'
    },
    {
        value: 'CR',
        label: 'Cost-reimbursable (CR)'
    },
    {
        value: 'CPFF',
        label: 'Cost-plus-fixed-fee (CPFF)'
    },
    {
        value: 'CPIF',
        label: 'Cost-plus-incentive-fee (CPIF)'
    },
    {
        value: 'CPAF',
        label: 'Cost-plus-award-fee (CPAF)'
    }
];
const CHOICE_PREFIX = 'choice:';
function isLineContractType(review) {
    return targetKind(review.targetObjectType) === 'line' && review.targetFieldPath.trim() === 'contract_type';
}
// Whether the field takes only a fixed set of values, so "Enter a different
// value" is never offered (a typed value outside the set would refuse).
export function hasClosedChoices(review) {
    // A condition set's branches are every answer its writer takes (code 30).
    if (review.reasonCode === ReviewReasonCode.CONDITION_BRANCH_UNRESOLVED) return true;
    return isLineContractType(review);
}
// An emitter's ISO day ("2030-12-30", quoted or not) as the reviewer reads a
// date: "30 Dec 2030".
export function readableDays(text) {
    return text.replace(/"?\b(\d{4}-\d{2}-\d{2})\b"?/g, (whole, day)=>formatReviewDay(day) ?? whole);
}
function jsonString(json) {
    try {
        const v = JSON.parse(json);
        return typeof v === 'string' ? v : '';
    } catch  {
        return '';
    }
}
// Every choice the reviewer has: the item's own candidates, then the rest of
// a closed field's values (each written by the same writer), so a line whose
// type no document states can still be typed in one click.
export function reviewChoices(review) {
    if (!hasClosedChoices(review)) return review.candidates;
    const have = new Set(review.candidates.map((c)=>jsonString(c.valueJson).toUpperCase()));
    const extra = CONTRACT_TYPE_CHOICES.filter((c)=>!have.has(c.value.toUpperCase())).map((c)=>new ReviewCandidate({
            key: `${CHOICE_PREFIX}${c.value}`,
            label: c.label,
            valueJson: JSON.stringify(c.value)
        }));
    return [
        ...review.candidates,
        ...extra
    ];
}
// The question the reviewer answers, naming the record. Empty for a finding
// with nothing to pick (it reads "What to do").
export function reviewQuestion(review, record) {
    const field = fieldLabel(review.targetFieldPath).toLowerCase();
    if (isCheckOnly(review) || review.reasonCode === ReviewReasonCode.LINE_SNAPSHOT_FROZEN) {
        return 'What to do';
    }
    switch(review.reasonCode){
        case ReviewReasonCode.CONTRACT_TYPE_CONFLICT:
            return `What contract type is ${record}?`;
        case ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES:
            return `Which ${field} is right for ${record}?`;
        case ReviewReasonCode.POP_NOT_READ:
            return `What is the ${field} of ${record}?`;
        case ReviewReasonCode.MOD_EFFECTIVE_DATE_NOT_STATED:
            return `When did ${record} take effect?`;
        case ReviewReasonCode.MOD_PURPOSE_UNDER_GATE:
            return `Which sentence states the purpose of ${record}?`;
        case ReviewReasonCode.MOD_TYPE_UNDER_GATE:
            return `What type of modification is ${record}?`;
        case ReviewReasonCode.CONDITION_BRANCH_UNRESOLVED:
            return 'Which of these conditions holds?';
        case ReviewReasonCode.POP_CHANGE_UNTARGETED:
            return `Which period's ${field} is this?`;
        default:
            break;
    }
    if (review.kind === ReviewItemKind.IDENTITY) return 'Pick the line, task or period this belongs to';
    return review.targetFieldPath.trim() ? `What is the right ${field} for ${record}?` : 'Your call';
}
// A long run of short codes ("5001AA, 5001AB, … 5001BN") inside an emitter's
// sentence, collapsed to its ends and a count. full is the text unchanged.
const LIST_RUN = /\b[A-Z0-9][A-Z0-9.-]{1,11}(?:, (?:and )?[A-Z0-9][A-Z0-9.-]{1,11}){5,}\b/g;
export function compactReason(text) {
    let collapsed = false;
    const short = text.replace(LIST_RUN, (run)=>{
        const parts = run.split(/, (?:and )?/);
        collapsed = true;
        return `${parts[0]} through ${parts[parts.length - 1]} (${parts.length} in all)`;
    });
    return {
        short,
        full: text,
        collapsed
    };
}
// The sub-lines an emitter named in its reason ("It also covers 5001AA,
// 5001AB, …, which state no type of their own"): the fallback when the
// program's lines are not loaded.
export function subLinesFromReason(review) {
    const m = /It also covers (.+?), which state no type/.exec(review.reason);
    if (!m) return [];
    return m[1].split(/, (?:and )?/).map((s)=>s.trim()).filter(Boolean);
}
// The sub-lines a contract-type decision also writes: pangaea's writer types
// the line and every current sub-line under it that states no type of its own
// (applyContractCLINContractType). Read from the program's lines when they are
// loaded, so the count is what the writer will touch.
export function contractTypeSubLines(review, lines) {
    if (!isLineContractType(review)) return [];
    const parent = review.targetLocator.trim().toUpperCase();
    if (!parent) return [];
    if (lines.length > 0) {
        return lines.filter((l)=>(l.parentClinNumber ?? '').trim().toUpperCase() === parent).filter((l)=>{
            const t = (l.contractType ?? '').trim().toUpperCase();
            return t === '' || t === 'OTHER';
        }).map((l)=>l.clinNumber);
    }
    return subLinesFromReason(review);
}
// ---- Evidence ------------------------------------------------------------
const MONTHS_LONG = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];
// The ways a value is printed in a document: a date in words and numbers, an
// amount with its commas, a string as it is.
function printedForms(json) {
    let v;
    try {
        v = JSON.parse(json);
    } catch  {
        return [];
    }
    if (typeof v === 'number') {
        return [
            v.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        ];
    }
    if (typeof v !== 'string' || !v.trim()) return [];
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
    if (!m) return v.length >= 3 ? [
        v.trim()
    ] : [];
    const month = MONTHS_LONG[Number(m[2]) - 1];
    if (!month) return [];
    const day = Number(m[3]);
    return [
        `${month} ${day}, ${m[1]}`,
        `${month.slice(0, 3)} ${day}, ${m[1]}`,
        `${Number(m[2])}/${day}/${m[1]}`
    ];
}
// The words that make a passage relevant to this item: its values as printed.
export function quoteNeedles(review, extraValues = []) {
    const values = [
        review.valueJson,
        ...review.candidates.map((c)=>c.valueJson),
        ...extraValues
    ].filter(Boolean);
    return [
        ...new Set(values.flatMap(printedForms))
    ];
}
const FOCUS_OVER = 280;
// A long passage cut to the sentences that hold the item's value, so the
// reviewer reads the line that matters ("Period of Performance (PoP) shall be
// September 23, 2025 through May 22, 2026.") and not the 600 characters around
// it. A short passage, or one no needle is in, is kept whole.
export function focusQuote(quote, needles) {
    if (quote.length <= FOCUS_OVER || needles.length === 0) return {
        text: quote,
        cut: false
    };
    const parts = quote.split(/(?<=[.;])\s+|\s*\|\s*|\n+/).filter((p)=>p.trim());
    const lower = needles.map((n)=>n.toLowerCase());
    const hit = parts.filter((p)=>lower.some((n)=>p.toLowerCase().includes(n)));
    if (hit.length === 0) return {
        text: quote,
        cut: false
    };
    const text = hit.join(' … ').trim();
    return {
        text,
        cut: text.length < quote.length
    };
}
function sameSource(a, b) {
    return a.page === b.page && a.quote.trim() === b.quote.trim();
}
// The item-level passages worth showing above the choices: each one not
// already shown under a choice. When the review carries none, the change
// item's own source reference (a modification's page and block) stands in.
export function itemSources(d, choices) {
    const r = d.review;
    const underChoices = choices.flatMap((c)=>c.sources);
    const own = r.sources.filter((s)=>!underChoices.some((u)=>sameSource(s, u)));
    if (r.sources.length > 0 || underChoices.length > 0) return own;
    const ref = reviewSourceProps(d, {
        after: '',
        before: ''
    });
    if (!ref.quote && !ref.page) return [];
    const { blockId, page } = parseSpan(ref.span);
    return [
        new ReviewSource({
            artifactId: ref.artifactId ?? '',
            page: ref.page || page || 0,
            quote: ref.quote ?? '',
            elementIds: blockId ? [
                blockId
            ] : []
        })
    ];
}
// What the evidence section says when there is no passage to show, instead
// of quoting something unrelated.
export function noEvidenceNote(review) {
    if (review.reasonCode === ReviewReasonCode.LINE_SNAPSHOT_FROZEN) {
        return 'Nothing to quote: this is about the import, not a passage in the document.';
    }
    if (review.reasonCode === ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES) {
        return 'The two values are worked out from the lines below, not quoted from one passage.';
    }
    if (!disagrees(review) || isCheckOnly(review)) {
        return 'No passage in the document states this.';
    }
    return 'Specra recorded no passage for this.';
}
// Whether the evidence shown is where we looked rather than what was stated:
// a value no document states.
export function evidenceIsWhereWeLooked(review) {
    return review.kind === ReviewItemKind.CONFLICT && !disagrees(review) && (NOT_STATED_CODES.has(review.reasonCode) || review.reasonCode === ReviewReasonCode.CONTRACT_TYPE_CONFLICT);
}
// ---- What the decision writes ---------------------------------------------
// Whether the reviewer only acknowledges this item: pangaea records it and
// writes nothing (zou projects reviewitem.AcknowledgeOnly as acknowledges).
// A frozen line snapshot is acknowledge-only wherever it is raised.
export function acknowledgesOnly(d) {
    if (d.review.candidates.length > 0) return false;
    return !!d.acknowledges || d.review.reasonCode === ReviewReasonCode.LINE_SNAPSHOT_FROZEN;
}
// "Settled by P00001" from pangaea's lineage rationale, or "" when a reviewer
// decided it.
export function settledBy(rationale) {
    const m = /^settled by (.+)$/i.exec(rationale.trim());
    if (!m) return '';
    const by = m[1].trim();
    if (/^code\b/i.test(by)) return 'Specra';
    return by;
}
// The sentence under Decision: what is written, where, and when. Never a bare
// state: "Accepted: contract type FFP on CLIN 5001 and its 36 sub-lines".
export function decisionSummary(d, opts) {
    const r = d.review;
    const state = d.resolutionState;
    const applied = state === 'Applied';
    if (state === 'Waived') return blankCopy(r).settled;
    if (state === 'Acknowledged' || acknowledgesOnly(d) && SETTLED_STATES.has(state)) {
        return 'Acknowledged. Nothing is written.';
    }
    const value = d.decidedValueJson ?? '';
    const by = settledBy(d.decisionRationale ?? '');
    const head = by ? `Settled by ${by}` : applied ? 'Applied' : state;
    if (!hasJsonValue(value)) {
        if (applied) return 'Applied.';
        if (r.kind === ReviewItemKind.IDENTITY && d.decisionRationale) {
            return `${head}: ${d.decisionRationale}. Will apply when committed.`;
        }
        return `${head}, but no value was recorded, so nothing will be written for it.`;
    }
    const field = r.targetFieldPath.trim();
    // A line's window decided on its start field sets both ends.
    const window = field === 'pop_start_date' && displayValue(value, field).includes(' – ');
    const what = window ? `period of performance ${displayValue(value, field)}` : field ? `${fieldLabel(field).toLowerCase()} ${displayValue(value, field)}` : displayValue(value, field);
    const n = opts.subLines ?? 0;
    // One decision for several lines (also_target_locators): name them all.
    const also = r.alsoTargetLocators ?? [];
    const where = also.length > 0 ? ` on ${opts.record} and ${also.length} more line${also.length === 1 ? '' : 's'} (${also.join(', ')})` : n > 0 ? ` on ${opts.record} and its ${n} sub-line${n === 1 ? '' : 's'}` : ` on ${opts.record}`;
    const when = applied ? ' Written.' : ' Writes when this review is committed.';
    return `${head}: ${what}${where}.${when}`;
}
